import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import ProgressWizard from 'components/field/ProgressWizard';
import Form from 'components/form/Form';
import Alert from 'components/global/Alert';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Icon from 'components/global/Icon';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Tabs from 'components/global/Tabs';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import WorkingHourTable, { workingHoursDummyData } from 'components/global/WorkingHourTable';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { BRANCHES } from 'services/consts';

import { getPickupLocationCountries } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { faAngleLeft, faAngleRight, faBoxCheck, faCircleInfo, faUserTag } from '@fortawesome/pro-solid-svg-icons';

import { BranchContactInfo } from './BranchContactInfo';
import { BranchLocations } from './BranchLocation';

/**
 * WARNING: When adding a new form field, please follow these steps:
 * 1. Add the new field to the 'allFields' array of the relevant tab in the 'tabs' definition.
 * 2. Ensure the new field is properly defined in the schema.
 * 3. If the new field requires specific handling, update the relevant component (e.g., BranchLocations, BranchContactInfo, etc.).
 *
 * Following these steps will ensure that the new field is included in the correct tab,
 * properly validated, and correctly handled in the form submission process.
 */

const BranchForm = NiceModal.create(({ id, mode, modalName }) => {
	const { Country } = useLocale();

	const [isMinimizing, setIsMinimizing] = useState(false);

	const [tab, setTab] = useState(0);
	const addMode = mode !== 'edit' && mode !== 'view';

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					// location
					name: yup.string().nullable().min(3).trim().required(),
					branchCode: yup.string().nullable(),
					address: yup.object().shape({
						text: yup.string().nullable().required(),
						countryName: yup.string().isCountryAllowed().nullable().required(),
						stateName: yup.string().nullable(),
						cityName: yup.string().nullable().required(),
						districtName: yup.string().nullable(),
						neighbourhoodName: yup.string().nullable(),
						street: yup.string().nullable(),
						buildingName: yup.string().nullable(),
						lat: yup.number().min(-90).max(90).nullable(),
						lng: yup.number().min(-180).max(180).nullable(),
						zipCode: yup.string().nullable(),
						addressInputType: yup.string().nullable()
					}),
					// contact Info
					person: yup.string().min(3).trim().nullable().required(),
					email: yup.string().email().nullable().required(),
					phone: yup.string().nullable().required().phoneNumber(),
					vatNumber: yup.string().nullable().checkVatNumber(),
					servingRadius: yup.number().positive().nullable().required(),
					storeID: yup.number().nullable(),
					autoShipment: yup.boolean(),
					branchUsers: yup.array().nullable(),
					driverUsers: yup.array().nullable(),

					// workimg Hours
					fullDayOption: yup.boolean().nullable(),
					workingHours: yup.array().of(
						yup
							.object()
							.shape({
								status: yup.boolean().required(),
								from: yup.string().nullable(),
								to: yup.string().nullable(),
								shift: yup.array().of(
									yup
										.object()
										.shape({
											start: yup.string().nullable(),
											end: yup.string().nullable()
										})
										.validShiftTimes()
								)
							})
							.noOverlappingHours()
					)
				})
				.nullable(),

		[]
	);
	const fillForm = useCallback(async () => {
		const defaultValues = {
			name: null,
			branchCode: null,
			address: {
				text: null,
				countryName: Country,
				stateName: null,
				cityName: null,
				districtName: null,
				neighbourhoodName: null,
				street: null,
				buildingName: null,
				zipCode: null,
				lat: null,
				lng: null,
				addressInputType: null
			},
			person: null,
			email: null,
			phone: null,
			vatNumber: null,
			servingRadius: 1,
			storeID: null,
			autoShipment: false,
			branchUsers: null,
			driverUsers: null,
			fullDayOption: null,
			workingHours: workingHoursDummyData
		};

		if (!id) return defaultValues;

		try {
			const response = await axiosPrivate.post(BRANCHES.GET_BRANCH, {
				ID: id
			});
			const { data, status } = response;

			if (status === 200) {
				return {
					...defaultValues,
					...data,
					name: data.name,
					fullDayOption: data.fullDayOption ?? false,
					address: {
						...data?.address,
						addressInputType: data.address.addressInputType ?? 'fullTextAddress'
					},
					workingHours: !data.workingHours.length
						? workingHoursDummyData
						: data.workingHours.map((wh) => ({
								...wh,
								...(!wh.shift?.length && {
									shift: [{ start: wh.from, end: wh.to === '23:59' ? '23:30' : wh.to }]
								})
							}))
				};
			} else {
				AppLogger.error(response);
				showError();
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
	}, [id]);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: fillForm
	});

	const {
		handleSubmit,
		watch,
		formState: { isDirty, isSubmitting, isSubmitSuccessful, dirtyFields, isLoading: isFormLoading, errors }
	} = formMethods;
	const isLoading = isSubmitting || isFormLoading;
	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful && !isMinimizing);

	const modal = useModal();
	const { closeModal } = useModalRouter();
	const axiosPrivate = useAxiosPrivate();
	const { showError, showSuccess } = useAlert();

	const viewMode = mode === 'view';
	const isAuthorized = useAccessControl();
	const isAuthorizedSLA = useMemo(() => isAuthorized('SLAModule'), []);

	const isFoodicsUser = isAuthorized('/sidebar/logo/foodics');

	const dispatch = useDispatch();

	const handleCloseModal = useCallback(() => {
		if (modalName) {
			closeModal();
		} else {
			modal.remove();
		}
	}, [modalName]);

	const nextStep = () => {
		const currentTabFields = tabs[tab].allFields;
		formMethods.trigger(currentTabFields, { shouldFocus: true }).then((isValid) => {
			if (isValid) {
				if (isAuthorizedSLA ? tab < 2 : tab < 1) {
					setTab(tab + 1);
				}
			}
		});
	};

	const prevStep = () => {
		if (tab > 0) {
			setTab(tab - 1);
		}
	};

	const tabs = useMemo(
		() => [
			{
				label: 'location',
				allFields: ['name', 'branchCode', 'address'],
				component: () => <BranchLocations mode={mode} isFoodicsUser={isFoodicsUser} />
			},
			{
				label: 'contactInfo',
				allFields: [
					'person',
					'email',
					'phone',
					'vatNumber',
					'servingRadius',
					'storeID',
					'autoShipment',
					'branchUsers',
					'driverUsers'
				],
				component: () => <BranchContactInfo mode={mode} />
			},
			...(isAuthorized('SLAModule')
				? [
						{
							label: 'workingHours',
							allFields: ['workingHours'],
							component: () => <WorkingHourTable mode={mode} />
						}
					]
				: [])
		],
		[isAuthorized, mode, errors]
	);

	const onSubmit = (data, e, pass) => {
		if (!Object.keys(dirtyFields).find((item) => item === 'workingHours') && !id && !pass) {
			NiceModal.show(ConfirmationModal, {
				title: 'youHaveUnchangedHours',
				subTitle: 'youHaveUnchangedHoursSubTitle',
				yesAction: () => onSubmit(data, {}, true),
				noAction: () => modal.remove()
			});
			return;
		}

		// ID means we are in edit mode else is add new branch

		const URL = id ? BRANCHES.UPDATE_BRANCH : BRANCHES.ADD_BRANCH;

		axiosPrivate
			.post(URL, {
				ID: id,
				...data,
				status: data?.status ?? 'active',
				workingHours: data.fullDayOption
					? undefined
					: data?.workingHours.map((item) => ({
							...item,
							from: item?.shift?.[0]?.start ?? '00:00',
							to: item?.shift?.[item.shift.length - 1]?.end ?? '23:59'
						}))
			})
			.then((response) => {
				const { status } = response;

				if (status === 200) {
					dispatch(updateDynamicListData(DYNAMICLISTS.BRANCHES));
					dispatch(getPickupLocationCountries({ invalidate: true }));
					handleCloseModal();
					showSuccess();
				}
			})
			.catch((error) => {
				if (error.response?.data?.code === 'error.F010') {
					showError({
						header: 'nameAlreadyExists',
						message: 'error.F010'
					});
				} else {
					AppLogger.error(error);
					showError({
						message: error?.response?.data?.code || error?.response?.data?.message
					});
				}
			});
	};
	const stages = useMemo(
		() => [
			{ name: 'location', icon: faUserTag },
			{ name: 'contactInfo', icon: faBoxCheck },
			...(isAuthorizedSLA ? [{ name: 'workingHours', icon: faBoxCheck }] : [])
		],
		[isAuthorizedSLA]
	);

	return (
		<Modal
			maxWidth="small"
			name={modalName}
			fullwidth
			className="modal-branch"
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={mode === 'edit' ? 'editBranch' : mode === 'view' ? 'viewBranch' : 'addBranch'}
					tooltip={['edit', 'view'].includes(mode) ? watch('name') : ''}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle style={{ display: 'flex' }}>
				<Typography variant="p1">
					{mode === 'edit' ? 'editBranch' : mode === 'view' ? 'viewBranch' : 'addBranch'}
				</Typography>
				<Tooltip title={'BranchForm.workinghours.description'}>
					<Icon icon={faCircleInfo} color="var(--content-4)" size="small" />
				</Tooltip>
			</ModalTitle>
			<ModalContent>
				{isFoodicsUser && (
					<Alert variant="warning" content="foodicsBranch.contentMessage" showIcon className="mb-4" />
				)}
				<Form formMethods={formMethods} borderless={tab === 2} roleName={tab === 2 && 'SLAModule'}>
					{!addMode ? (
						<Tabs data={tabs} />
					) : (
						<>
							<ProgressWizard stages={stages} setIndex={setTab} index={tab} gutterBottom />
							{tabs[tab].component()}
						</>
					)}
				</Form>
			</ModalContent>
			{!viewMode && (
				<ModalActions>
					<Button label="cancel" variant="outline" onClick={handleCloseModal} />
					{!addMode ? (
						<Button
							loading={isLoading}
							disabled={!isDirty}
							disabledTooltip={!isDirty && 'formInfo.noChanges'}
							label={!addMode ? 'save' : 'button.add'}
							onClick={handleSubmit(onSubmit, AppLogger.log)}
						/>
					) : (
						<>
							{tab !== 0 && (
								<Button
									label="previous"
									variant="outline"
									icon={faAngleLeft}
									onClick={() => prevStep()}
								/>
							)}
							{tab === 0 || (tab === 1 && isAuthorizedSLA) ? (
								<Button
									icon={faAngleRight}
									label="continue"
									iconPos="right"
									onClick={nextStep}
									variant="blue"
									loading={isLoading}
								/>
							) : null}
							{tab === 2 || (tab === 1 && !isAuthorizedSLA) ? (
								<Button
									loading={isLoading}
									label="button.add"
									disabled={tab === 1 ? !isDirty : false}
									disabledTooltip={!isDirty && 'formInfo.noChanges'}
									onClick={handleSubmit(onSubmit, AppLogger.error)}
									id="SaveWarehouseFormButton"
								/>
							) : null}
						</>
					)}
				</ModalActions>
			)}
		</Modal>
	);
});

export default BranchForm;
